import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useServerConfig } from '../context/ServerConfigContext';
import './NaverFinancePage.css';

const NaverFinancePage = () => {
  const { getApiUrl } = useServerConfig();

  // 컨트롤 상태
  const [limit, setLimit] = useState(10);
  const [includeStocks, setIncludeStocks] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const autoRefreshRef = useRef(null);

  // 데이터 상태
  const [upjongData, setUpjongData] = useState([]);
  const [themeData, setThemeData] = useState([]);
  const [upjongFetchedAt, setUpjongFetchedAt] = useState('');
  const [themeFetchedAt, setThemeFetchedAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [crawling, setCrawling] = useState(false);

  // 이력 상태
  const [historyList, setHistoryList] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState('latest');

  // 종목 펼치기 상태
  const [expandedUpjong, setExpandedUpjong] = useState(new Set());
  const [expandedTheme, setExpandedTheme] = useState(new Set());
  const [stocksCache, setStocksCache] = useState({});
  const [stocksLoading, setStocksLoading] = useState({});

  // 모바일 탭
  const [mobileTab, setMobileTab] = useState('upjong');

  // 등락률 색상
  const getChangeColor = (rate) => {
    if (rate > 0) return '#e74c3c';
    if (rate < 0) return '#3498db';
    return '#95a5a6';
  };

  const getChangeIcon = (rate) => {
    if (rate > 0) return '\u25B2';
    if (rate < 0) return '\u25BC';
    return '\u2500';
  };

  const formatRate = (rate) => {
    if (rate === null || rate === undefined) return '-';
    const prefix = rate > 0 ? '+' : '';
    return `${prefix}${rate.toFixed(2)}%`;
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return Number(price).toLocaleString();
  };

  const formatPriceChange = (change, rate) => {
    if (change === null || change === undefined) return '-';
    const icon = getChangeIcon(rate || change);
    return `${icon}${Math.abs(change).toLocaleString()}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    } catch {
      return dateStr;
    }
  };

  // 이력 목록 로드
  const fetchHistory = useCallback(async () => {
    try {
      const url = getApiUrl('naver-finance-api-server', '/market-overview/history') + '?limit=20';
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setHistoryList(data.data || []);
      }
    } catch (err) {
      console.error('이력 로드 실패:', err);
    }
  }, [getApiUrl]);

  // GET - DB 최신 데이터 조회
  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const url = getApiUrl('naver-finance-api-server', '/market-overview') +
        `?limit=${limit}&include_stocks=${includeStocks}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setUpjongData(data.upjong?.data || []);
        setThemeData(data.theme?.data || []);
        setUpjongFetchedAt(data.upjong?.fetched_at || '');
        setThemeFetchedAt(data.theme?.fetched_at || '');
      }
    } catch (err) {
      console.error('데이터 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [getApiUrl, limit, includeStocks]);

  // POST - 네이버 실시간 크롤링
  const fetchCrawl = async () => {
    setCrawling(true);
    try {
      const url = getApiUrl('naver-finance-api-server', '/market-overview') +
        `?limit=${limit}&include_stocks=${includeStocks}`;
      const res = await fetch(url, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setUpjongData(data.upjong?.data || []);
        setThemeData(data.theme?.data || []);
        setUpjongFetchedAt(data.upjong?.fetched_at || '');
        setThemeFetchedAt(data.theme?.fetched_at || '');
        // 이력 갱신
        fetchHistory();
      }
    } catch (err) {
      console.error('크롤링 실패:', err);
    } finally {
      setCrawling(false);
    }
  };

  // 특정 시점 데이터 조회
  const fetchHistoryData = async (fetchedAt) => {
    setLoading(true);
    try {
      const url = getApiUrl('naver-finance-api-server', `/market-overview/history/${encodeURIComponent(fetchedAt)}`) +
        `?limit=${limit}&include_stocks=${includeStocks}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setUpjongData(data.upjong?.data || []);
        setThemeData(data.theme?.data || []);
        setUpjongFetchedAt(data.upjong?.fetched_at || '');
        setThemeFetchedAt(data.theme?.fetched_at || '');
      }
    } catch (err) {
      console.error('이력 데이터 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  // 개별 종목 로드 (include_stocks=false 시)
  const fetchGroupStocks = async (groupType, naverNo) => {
    const cacheKey = `${groupType}_${naverNo}`;
    if (stocksCache[cacheKey]) return;

    setStocksLoading(prev => ({ ...prev, [cacheKey]: true }));
    try {
      const endpoint = groupType === 'upjong'
        ? `/upjong/${naverNo}/stocks`
        : `/theme/${naverNo}/stocks`;
      const url = getApiUrl('naver-finance-api-server', endpoint);
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setStocksCache(prev => ({ ...prev, [cacheKey]: data.data || [] }));
      }
    } catch (err) {
      console.error('종목 조회 실패:', err);
    } finally {
      setStocksLoading(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  // 종목 펼치기/접기 토글
  const toggleExpand = (groupType, naverNo, hasStocks) => {
    const setExpanded = groupType === 'upjong' ? setExpandedUpjong : setExpandedTheme;
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(naverNo)) {
        next.delete(naverNo);
      } else {
        next.add(naverNo);
        if (!hasStocks) {
          fetchGroupStocks(groupType, naverNo);
        }
      }
      return next;
    });
  };

  // 종목 리스트 가져오기 (데이터 내장 또는 캐시)
  const getStocks = (groupType, item) => {
    if (item.stocks && item.stocks.length > 0) return item.stocks;
    const cacheKey = `${groupType}_${item.naver_no}`;
    return stocksCache[cacheKey] || [];
  };

  // 페이지 초기 로드
  useEffect(() => {
    fetchLatest();
    fetchHistory();
  }, [fetchLatest, fetchHistory]);

  // 자동새로고침
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        fetchLatest();
      }, 60000);
    } else {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
      }
    }
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    };
  }, [autoRefresh, fetchLatest]);

  // 이력 선택 핸들러
  const handleHistoryChange = (value) => {
    setSelectedHistory(value);
    if (value === 'latest') {
      fetchLatest();
    } else {
      fetchHistoryData(value);
    }
  };

  // 종목 테이블 렌더링
  const renderStockTable = (stocks, groupType) => {
    if (!stocks || stocks.length === 0) return null;
    return (
      <div className="nf-stock-table-wrapper">
        <table className="nf-stock-table">
          <thead>
            <tr>
              <th>종목명</th>
              <th>현재가</th>
              <th>전일비</th>
              <th>등락률</th>
            </tr>
          </thead>
          <tbody>
            {stocks.map((stock, idx) => (
              <tr key={stock.stock_code || idx}>
                <td className="nf-stock-name">
                  {stock.stock_name}
                  {groupType === 'theme' && stock.theme_reason && (
                    <div className="nf-theme-reason">{stock.theme_reason}</div>
                  )}
                </td>
                <td className="nf-stock-price">{formatPrice(stock.current_price)}</td>
                <td style={{ color: getChangeColor(stock.change_rate) }}>
                  {formatPriceChange(stock.price_change, stock.change_rate)}
                </td>
                <td style={{ color: getChangeColor(stock.change_rate), fontWeight: 600 }}>
                  {formatRate(stock.change_rate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // 업종 카드 렌더링
  const renderUpjongCard = (item, index) => {
    const isExpanded = expandedUpjong.has(item.naver_no);
    const hasInlineStocks = item.stocks && item.stocks.length > 0;
    const stocks = getStocks('upjong', item);
    const cacheKey = `upjong_${item.naver_no}`;
    const isLoadingStocks = stocksLoading[cacheKey];

    return (
      <div key={item.naver_no || index} className="nf-card">
        <div className="nf-card-header" onClick={() => toggleExpand('upjong', item.naver_no, hasInlineStocks)}>
          <div className="nf-card-rank">{index + 1}</div>
          <div className="nf-card-info">
            <div className="nf-card-name">{item.name}</div>
            <div className="nf-card-stats">
              <span className="nf-stat-rise">{'\u25B2'}{item.rise_count}</span>
              <span className="nf-stat-stable">{'\u2500'}{item.stable_count}</span>
              <span className="nf-stat-fall">{'\u25BC'}{item.fall_count}</span>
              <span className="nf-stat-total">({item.total_count || (item.rise_count + item.stable_count + item.fall_count)})</span>
            </div>
          </div>
          <div className="nf-card-rate" style={{ color: getChangeColor(item.change_rate) }}>
            {formatRate(item.change_rate)}
          </div>
          <div className="nf-card-toggle">{isExpanded ? '\u25BC' : '\u25B6'}</div>
        </div>
        {isExpanded && (
          <div className="nf-card-body">
            {isLoadingStocks ? (
              <div className="nf-loading-stocks">종목 로딩 중...</div>
            ) : stocks.length > 0 ? (
              renderStockTable(stocks, 'upjong')
            ) : (
              <div className="nf-no-stocks">종목 데이터가 없습니다</div>
            )}
          </div>
        )}
      </div>
    );
  };

  // 테마 카드 렌더링
  const renderThemeCard = (item, index) => {
    const isExpanded = expandedTheme.has(item.naver_no);
    const hasInlineStocks = item.stocks && item.stocks.length > 0;
    const stocks = getStocks('theme', item);
    const cacheKey = `theme_${item.naver_no}`;
    const isLoadingStocks = stocksLoading[cacheKey];

    return (
      <div key={item.naver_no || index} className="nf-card nf-theme-card">
        <div className="nf-card-header" onClick={() => toggleExpand('theme', item.naver_no, hasInlineStocks)}>
          <div className="nf-card-rank">{index + 1}</div>
          <div className="nf-card-info">
            <div className="nf-card-name">{item.name}</div>
            <div className="nf-card-stats">
              <span className="nf-stat-rise">{'\u25B2'}{item.rise_count}</span>
              <span className="nf-stat-stable">{'\u2500'}{item.stable_count}</span>
              <span className="nf-stat-fall">{'\u25BC'}{item.fall_count}</span>
              {item.avg_3day_change !== null && item.avg_3day_change !== undefined && (
                <span className="nf-stat-avg" style={{ color: getChangeColor(item.avg_3day_change) }}>
                  3일avg {formatRate(item.avg_3day_change)}
                </span>
              )}
            </div>
            {(item.leading_stock1_name || item.leading_stock2_name) && (
              <div className="nf-leading-stocks">
                주도주: {[item.leading_stock1_name, item.leading_stock2_name].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
          <div className="nf-card-rate" style={{ color: getChangeColor(item.change_rate) }}>
            {formatRate(item.change_rate)}
          </div>
          <div className="nf-card-toggle">{isExpanded ? '\u25BC' : '\u25B6'}</div>
        </div>
        {isExpanded && (
          <div className="nf-card-body">
            {isLoadingStocks ? (
              <div className="nf-loading-stocks">종목 로딩 중...</div>
            ) : stocks.length > 0 ? (
              renderStockTable(stocks, 'theme')
            ) : (
              <div className="nf-no-stocks">종목 데이터가 없습니다</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="nf-page">
      {/* 상단 컨트롤 */}
      <div className="nf-controls">
        <h2 className="nf-title">시장 Overview</h2>
        <div className="nf-control-row">
          <div className="nf-control-group">
            <label>건수:</label>
            <select value={limit} onChange={e => setLimit(Number(e.target.value))}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>
          <div className="nf-control-group">
            <label className="nf-checkbox-label">
              <input
                type="checkbox"
                checked={includeStocks}
                onChange={e => setIncludeStocks(e.target.checked)}
              />
              종목포함
            </label>
          </div>
          <button
            className="nf-btn nf-btn-crawl"
            onClick={fetchCrawl}
            disabled={crawling}
          >
            {crawling ? '크롤링 중...' : '조회'}
          </button>
          <button
            className={`nf-btn nf-btn-refresh ${autoRefresh ? 'active' : ''}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? '자동새로고침 60초 \u23F8' : '자동새로고침 \u25B6'}
          </button>
        </div>
        <div className="nf-control-row nf-control-row-secondary">
          <div className="nf-control-group">
            <label>조회 시점:</label>
            <select
              value={selectedHistory}
              onChange={e => handleHistoryChange(e.target.value)}
              className="nf-history-select"
            >
              <option value="latest">최신</option>
              {historyList.map((h, idx) => (
                <option key={idx} value={h.fetched_at}>
                  {formatDateTime(h.fetched_at)} (업{h.upjong_count || '-'}/테{h.theme_count || '-'})
                </option>
              ))}
            </select>
          </div>
          {(upjongFetchedAt || themeFetchedAt) && (
            <div className="nf-last-fetched">
              마지막 조회: {formatDateTime(upjongFetchedAt)} (업종) / {formatDateTime(themeFetchedAt)} (테마)
            </div>
          )}
        </div>
      </div>

      {/* 로딩 */}
      {(loading || crawling) && (
        <div className="nf-loading-bar">
          {crawling ? '네이버에서 데이터를 수집 중입니다...' : '데이터를 불러오는 중...'}
        </div>
      )}

      {/* 모바일 탭 */}
      <div className="nf-mobile-tabs">
        <button
          className={`nf-mobile-tab ${mobileTab === 'upjong' ? 'active' : ''}`}
          onClick={() => setMobileTab('upjong')}
        >
          업종 ({upjongData.length})
        </button>
        <button
          className={`nf-mobile-tab ${mobileTab === 'theme' ? 'active' : ''}`}
          onClick={() => setMobileTab('theme')}
        >
          테마 ({themeData.length})
        </button>
      </div>

      {/* 메인 콘텐츠 - 2컬럼 */}
      <div className="nf-content">
        <div className={`nf-column nf-column-upjong ${mobileTab !== 'upjong' ? 'nf-hidden-mobile' : ''}`}>
          <h3 className="nf-column-title">업종 ({upjongData.length})</h3>
          {upjongData.length === 0 && !loading ? (
            <div className="nf-empty">데이터가 없습니다. 조회 버튼을 눌러주세요.</div>
          ) : (
            upjongData.map((item, idx) => renderUpjongCard(item, idx))
          )}
        </div>
        <div className={`nf-column nf-column-theme ${mobileTab !== 'theme' ? 'nf-hidden-mobile' : ''}`}>
          <h3 className="nf-column-title">테마 ({themeData.length})</h3>
          {themeData.length === 0 && !loading ? (
            <div className="nf-empty">데이터가 없습니다. 조회 버튼을 눌러주세요.</div>
          ) : (
            themeData.map((item, idx) => renderThemeCard(item, idx))
          )}
        </div>
      </div>

      {/* 하단 정보 */}
      <div className="nf-footer">
        업종 {upjongData.length}개 / 테마 {themeData.length}개 조회완료
      </div>
    </div>
  );
};

export default NaverFinancePage;
